/** 为设计师编辑器提供布局网格，依赖画板数据及 Canvas 舞台的定位、缩放能力 */
import React, { useEffect, useState } from 'react';
import { BaseScaleView, BaseStagePlugin, useActiveFrameId, useFramework } from '@/framework';
import { PluginType, type IExportPlugin, type IShape } from '@/version-types';
import stageGridPluginState, { useStageGridStore } from './models/stage-grid-state';
import { StageGridPlugin } from './stage-grid';
import GridLineOperate from './views/grid-lines/grid-line-operate';
import GridLines from './views/grid-lines/grid-lines';

const StageGridPluginView = React.memo(() => {
    const activeFrameId = useActiveFrameId();
    const { frame, flatLayer, events } = useFramework().service;
    const [frameShape, setFrameShape] = useState<IShape | null>(null);

    useEffect(() => {
        const syncFrame = () => {
            setFrameShape(frame.getBackgroundLayer(activeFrameId) ? frame.getShape(activeFrameId) : null);
        };
        syncFrame();
        // 插件会先于模板导入挂载，需在画板加载、切换及尺寸变化后重新读取几何信息。
        const renderHandle = frame.onRerenderAll(syncFrame);
        const layoutHandle = frame.onFrameLayoutComplete(syncFrame);
        const resizeHandle = frame.onResize(syncFrame);
        const layerHandle = flatLayer.onChange((layer) => {
            if (layer && layer.id === frame.getBackgroundLayer(activeFrameId)?.id) {
                syncFrame();
            }
        });

        return () => {
            events.off(renderHandle);
            events.off(layoutHandle);
            events.off(resizeHandle);
            events.off(layerHandle);
        };
    }, [activeFrameId, frame, flatLayer, events]);

    const showGridLineOperate = useStageGridStore()((state) => state.showGridLineOperate);
    const resetVersion = useStageGridStore()((state) => state.resetVersion);
    if (!showGridLineOperate) {
        return null;
    }
    return (
        <div className="stage-grid-plugin-wrap">
            <GridLineOperate key={resetVersion} />
            <BaseStagePlugin name="stage-grid" zIndex={1}>
                {frameShape && (
                    <BaseScaleView layer={frameShape}>
                        <GridLines width={frameShape.width} height={frameShape.height} />
                    </BaseScaleView>
                )}
            </BaseStagePlugin>
        </div>
    );
});

StageGridPluginView.displayName = 'StageGridPluginView';

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: -1,
    name: 'stage-grid-plugin',
    render() {
        return <StageGridPluginView />;
    },
    createInstance({ identity }) {
        return [{ key: 'StageGridPlugin', value: new StageGridPlugin(identity) }];
    },
    createState() {
        return [stageGridPluginState];
    },
    destroy({ getInstance }) {
        getInstance<StageGridPlugin>('StageGridPlugin').reset();
    },
};

export default exportPlugin;
